import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useRecoilValue } from "recoil";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import { DataGridPro, type GridColDef } from "@mui/x-data-grid-pro";

import { LoginUserAtom } from "../stores/LoginUserAtom";
import ConstList from "../utils/ConstList";
import { designTokens } from "../theme";

type CustomerType = "prospective" | "existing";
type CustomerTypeFilter = "all" | CustomerType;

type ApiSalesReport = {
  sales_report_id?: number | string;
  id?: number | string;
  company_id?: number | string;
  company_name?: string;
  contact_person?: string;
  sales_person?: string;
  sales_date?: string;
  sales_approach_method?: string;
  proposal_details?: string;
  sales_review?: string;
  sales_status?: string;
  next_action?: string;
  next_action_details?: string;
  next_response_details?: string;
  created_at?: string;
  registered_at?: string;
  project_names?: string[] | string;
  projects?: Array<{ project_name?: string; name?: string }>;
  sales_method_other?: string;
  purchase_intention?: string;
  proposed_service?: string;
  lead_sources?: string;
  referrer?: string;
  customer_needs?: string;
  competitor_information?: string;
  request_inquiry?: string;
  issue_complaint?: string;
  follow_up_deadline?: string;
  response_deadline?: string;
  next_schedule?: string;
  man_hours?: number | string;
};

type SalesReportRow = {
  id: string;
  customerType: CustomerType;
  salesDate: string;
  companyName: string;
  projectNames: string[];
  contactPerson: string;
  salesPerson: string;
  approachMethod: string;
  reportDetails: string;
  salesStatus: string;
  nextAction: string;
  detail: ApiSalesReport;
  sortKey: string;
};

const toRows = (
  list: ApiSalesReport[] | undefined,
  customerType: CustomerType,
): SalesReportRow[] =>
  (list ?? []).map((item, index) => {
    const sourceId =
      item.sales_report_id ??
      item.id ??
      `${item.company_id ?? "company"}-${item.sales_date ?? "date"}-${index}`;
    const numericId = Number(sourceId);
    const idSortValue = Number.isFinite(numericId)
      ? String(numericId).padStart(16, "0")
      : String(sourceId);

    const projectNames = Array.isArray(item.projects)
      ? item.projects.map((project) =>
          String(project.project_name ?? project.name ?? ""),
        )
      : Array.isArray(item.project_names)
        ? item.project_names.map(String)
        : String(item.project_names ?? "")
            .split(",")
            .map((name) => name.trim())
            .filter(Boolean);

    return {
      id: `${customerType}-${sourceId}`,
      customerType,
      salesDate: String(item.sales_date ?? ""),
      companyName: String(item.company_name ?? ""),
      projectNames,
      contactPerson: String(item.contact_person ?? ""),
      salesPerson: String(item.sales_person ?? ""),
      approachMethod: [item.sales_approach_method, item.sales_method_other]
        .filter((value) => String(value ?? "").trim() !== "")
        .join("（") + (item.sales_method_other ? "）" : ""),
      reportDetails: String(item.proposal_details ?? ""),
      salesStatus: String(item.sales_status ?? ""),
      nextAction: String(
        item.next_action_details ??
          item.next_response_details ??
          item.next_action ??
          "",
      ),
      detail: item,
      sortKey: `${item.created_at ?? item.registered_at ?? item.sales_date ?? ""}-${idSortValue}`,
    };
  });

const formatDate = (value: string) => {
  if (!value) return "—";

  const [year, month, day] = value.slice(0, 10).split("-");
  return year && month && day ? `${year}/${month}/${day}` : value;
};

export default function SalesReportList() {
  const loginUserData = useRecoilValue(LoginUserAtom);
  const navigate = useNavigate();
  const [rows, setRows] = useState<SalesReportRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [keyword, setKeyword] = useState("");
  const [customerTypeFilter, setCustomerTypeFilter] =
    useState<CustomerTypeFilter>("all");
  const [companyFilter, setCompanyFilter] = useState("all");
  const [projectFilter, setProjectFilter] = useState("all");
  const [selectedReport, setSelectedReport] = useState<{
    row: SalesReportRow;
    report: ApiSalesReport;
  } | null>(null);

  const fetchReports = useCallback(async () => {
    if (!loginUserData.accessToken) {
      return;
    }

    try {
      setLoading(true);
      setErrorMessage(null);

      const request = (customerType: CustomerType) =>
        axios.post(
          ConstList.getApiUrl() + "ProspectiveSalesReport.php",
          {
            flag: "Pros_Sales_Report",
            customer_type: customerType,
            access_token: loginUserData.accessToken,
            algorithm: loginUserData.algorithm,
          },
          ConstList.AXIOS_CONFIG,
        );

      const [prospectiveResponse, existingResponse] = await Promise.all([
        request("prospective"),
        request("existing"),
      ]);

      const getList = (response: typeof prospectiveResponse) => {
        if (response.data?.data === false) {
          throw new Error(
            response.data?.message ?? "日報一覧の取得に失敗しました。",
          );
        }

        return response.data?.data?.list ?? [];
      };

      const prospectiveRows = toRows(
        getList(prospectiveResponse),
        "prospective",
      );
      const existingRows = toRows(
        getList(existingResponse),
        "existing",
      );

      setRows(
        [...prospectiveRows, ...existingRows].sort((a, b) =>
          b.sortKey.localeCompare(a.sortKey),
        ),
      );
    } catch (error) {
      console.error("日報一覧取得エラー", error);
      setRows([]);
      setErrorMessage("日報一覧の取得に失敗しました。");
    } finally {
      setLoading(false);
    }
  }, [loginUserData.accessToken, loginUserData.algorithm]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const filteredRows = useMemo(() => {
    const normalizedKeyword = keyword.trim().toLocaleLowerCase("ja");

    return rows.filter((row) => {
      if (
        customerTypeFilter !== "all" &&
        row.customerType !== customerTypeFilter
      ) {
        return false;
      }

      if (companyFilter !== "all" && row.companyName !== companyFilter) {
        return false;
      }

      if (
        projectFilter !== "all" &&
        !row.projectNames.includes(projectFilter)
      ) {
        return false;
      }

      if (!normalizedKeyword) return true;

      return [
        row.companyName,
        ...row.projectNames,
        row.contactPerson,
        row.salesPerson,
        row.approachMethod,
        row.reportDetails,
        row.salesStatus,
        row.nextAction,
      ].some((value) =>
        value.toLocaleLowerCase("ja").includes(normalizedKeyword),
      );
    });
  }, [companyFilter, customerTypeFilter, keyword, projectFilter, rows]);

  const companyOptions = useMemo(
    () =>
      [...new Set(rows.map((row) => row.companyName).filter(Boolean))].sort(
        (a, b) => a.localeCompare(b, "ja"),
      ),
    [rows],
  );

  const projectOptions = useMemo(
    () =>
      [
        ...new Set(
          rows
            .filter(
              (row) =>
                companyFilter === "all" || row.companyName === companyFilter,
            )
            .flatMap((row) => row.projectNames),
        ),
      ].sort((a, b) => a.localeCompare(b, "ja")),
    [companyFilter, rows],
  );

  const columns = useMemo<GridColDef<SalesReportRow>[]>(
    () => [
      {
        field: "sortKey",
        headerName: "並び順",
      },
      {
        field: "salesDate",
        headerName: "実施日",
        width: 120,
        renderCell: (params) => formatDate(params.row.salesDate),
      },
      {
        field: "customerType",
        headerName: "顧客区分",
        width: 120,
        sortable: false,
        renderCell: (params) => (
          <Chip
            size="small"
            label={
              params.row.customerType === "prospective"
                ? "見込み顧客"
                : "既存顧客"
            }
            sx={{
              color:
                params.row.customerType === "prospective"
                  ? designTokens.brand.dark
                  : "#2E5F9E",
              backgroundColor:
                params.row.customerType === "prospective"
                  ? designTokens.overlay.brandSoft
                  : "rgba(66, 133, 244, 0.10)",
              fontWeight: 800,
            }}
          />
        ),
      },
      {
        field: "companyName",
        headerName: "会社名",
        minWidth: 210,
        flex: 1,
      },
      {
        field: "projectNames",
        headerName: "案件名",
        minWidth: 240,
        flex: 1,
        sortable: false,
        renderCell: (params) => (
          <Stack
            direction="row"
            spacing={0.5}
            useFlexGap
            flexWrap="wrap"
            py={0.5}
          >
            {params.row.projectNames.map((name) => (
              <Chip key={name} label={name} size="small" variant="outlined" />
            ))}
          </Stack>
        ),
      },
      {
        field: "contactPerson",
        headerName: "顧客窓口",
        minWidth: 150,
        flex: 0.7,
      },
      {
        field: "salesPerson",
        headerName: "営業担当者",
        width: 130,
      },
      {
        field: "nextAction",
        headerName: "次回対応",
        minWidth: 220,
        flex: 1,
      },
    ],
    [],
  );

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 1.5, md: 2.5 },
        borderRadius: 3,
        backgroundColor: designTokens.surface.paper,
        border: `1px solid ${designTokens.border.default}`,
        boxShadow: "0 8px 24px rgba(15,23,42,0.05)",
      }}
    >
      <Stack spacing={2}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          alignItems={{ xs: "stretch", md: "center" }}
          justifyContent="space-between"
          spacing={1.5}
        >
          <Box>
            <Typography
              component="h2"
              sx={{
                color: designTokens.text.primary,
                fontSize: 18,
                fontWeight: 900,
              }}
            >
              日報一覧
            </Typography>
            <Typography
              sx={{
                mt: 0.4,
                color: designTokens.text.secondary,
                fontSize: 13,
              }}
            >
              新しい日報から順に表示しています。
            </Typography>
          </Box>

          <Button
            variant="outlined"
            startIcon={
              loading ? <CircularProgress size={16} /> : <RefreshRoundedIcon />
            }
            disabled={loading}
            onClick={fetchReports}
            sx={{
              color: designTokens.brand.dark,
              borderColor: designTokens.border.strong,
              fontWeight: 800,
            }}
          >
            再読み込み
          </Button>
        </Stack>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
          <TextField
            select
            size="small"
            label="会社名"
            value={companyFilter}
            onChange={(event) => {
              setCompanyFilter(event.target.value);
              setProjectFilter("all");
            }}
            sx={{ minWidth: { sm: 210 } }}
          >
            <MenuItem value="all">すべての会社</MenuItem>
            {companyOptions.map((companyName) => (
              <MenuItem key={companyName} value={companyName}>
                {companyName}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            size="small"
            label="案件名"
            value={projectFilter}
            onChange={(event) => setProjectFilter(event.target.value)}
            sx={{ minWidth: { sm: 220 } }}
          >
            <MenuItem value="all">すべての案件</MenuItem>
            {projectOptions.map((projectName) => (
              <MenuItem key={projectName} value={projectName}>
                {projectName}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            size="small"
            label="顧客区分"
            value={customerTypeFilter}
            onChange={(event) =>
              setCustomerTypeFilter(event.target.value as CustomerTypeFilter)
            }
            sx={{ minWidth: { sm: 180 } }}
          >
            <MenuItem value="all">すべて</MenuItem>
            <MenuItem value="prospective">見込み顧客</MenuItem>
            <MenuItem value="existing">既存顧客</MenuItem>
          </TextField>
        </Stack>

        {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

        <Box sx={{ height: 640, width: "100%" }}>
          <Typography
            variant="body2"
            sx={{ mb: 1, color: designTokens.text.secondary, fontWeight: 700 }}
          >
            総件数：{filteredRows.length}件
          </Typography>
          <DataGridPro
            rows={filteredRows}
            columns={columns}
            onRowDoubleClick={(params) =>
              navigate(
                `/sales-reports?view=detail&id=${encodeURIComponent(
                  String(params.row.detail.sales_record_id ?? params.row.id),
                )}&customer_type=${params.row.customerType}`,
              )
            }
            loading={loading}
            disableRowSelectionOnClick
            hideFooter
            initialState={{
              sorting: {
                sortModel: [{ field: "sortKey", sort: "desc" }],
              },
              columns: {
                columnVisibilityModel: {
                  sortKey: false,
                },
              },
            }}
            localeText={{
              noRowsLabel: "登録済みの日報はありません。",
              noResultsOverlayLabel: "検索条件に一致する日報はありません。",
            }}
            sx={{
              borderColor: designTokens.border.default,
              color: designTokens.text.primary,
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: designTokens.surface.subtle,
                borderBottomColor: designTokens.border.strong,
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                fontWeight: 800,
              },
              "& .MuiDataGrid-row:hover": {
                backgroundColor: designTokens.overlay.brandSoft,
              },
              "& .MuiDataGrid-cell": {
                borderBottomColor: designTokens.border.default,
              },
            }}
          />
        </Box>
      </Stack>

      <Dialog
        open={selectedReport !== null}
        onClose={() => setSelectedReport(null)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {selectedReport?.row.customerType === "prospective"
            ? "見込み顧客の日報詳細"
            : "既存顧客の日報詳細"}
        </DialogTitle>
        <DialogContent dividers>
          {selectedReport && (
            <Stack spacing={1.5}>
              <DetailLine label="実施日" value={formatDate(selectedReport.report.sales_date ?? "")} />
              <DetailLine label="会社名" value={selectedReport.report.company_name} />
              <DetailLine label="案件名" value={selectedReport.row.projectNames.join("、")} />
              <DetailLine label="顧客窓口" value={selectedReport.report.contact_person} />
              <DetailLine label="営業担当者" value={selectedReport.report.sales_person} />
              <Divider />
                  <DetailLine label="営業・対応方法" value={[selectedReport.report.sales_approach_method, selectedReport.report.sales_method_other].filter((value) => String(value ?? "").trim() !== "").join("（") + (selectedReport.report.sales_method_other ? "）" : "")} />
              <DetailLine label="対応時間" value={selectedReport.report.man_hours == null ? "" : `${selectedReport.report.man_hours}分`} />
              {selectedReport.row.customerType === "prospective" ? (
                <>
                  <DetailLine label="流入経路" value={selectedReport.report.lead_sources} />
                  <DetailLine label="紹介元" value={selectedReport.report.referrer} />
                  <DetailLine label="購買意欲" value={selectedReport.report.purchase_intention} />
                  <DetailLine label="提案サービス" value={selectedReport.report.proposed_service} />
                  <DetailLine label="課題・ニーズ" value={selectedReport.report.customer_needs} multiline />
                  <DetailLine label="競合情報" value={selectedReport.report.competitor_information} multiline />
                  <DetailLine label="提案内容" value={selectedReport.report.proposal_details} multiline />
                  <DetailLine label="営業後の進捗状況" value={selectedReport.report.sales_status} />
                  <DetailLine label="次回アクション" value={selectedReport.report.next_action} />
                  <DetailLine label="次回予定" value={selectedReport.report.follow_up_deadline || selectedReport.report.response_deadline || selectedReport.report.next_schedule} />
                </>
              ) : (
                <>
                  <DetailLine label="要望・問い合わせ" value={selectedReport.report.request_inquiry} multiline />
                  <DetailLine label="問題・クレーム" value={selectedReport.report.issue_complaint} multiline />
                  <DetailLine label="対応内容" value={selectedReport.report.proposal_details} multiline />
                  <DetailLine label="次回対応" value={selectedReport.report.next_response_details || selectedReport.report.next_action_details} multiline />
                  <DetailLine label="対応期限" value={selectedReport.report.response_deadline || selectedReport.report.follow_up_deadline || selectedReport.report.next_schedule} />
                </>
              )}
              <Divider />
              <DetailLine label="振り返りメモ" value={selectedReport.report.sales_review} multiline />
            </Stack>
          )}
        </DialogContent>
      </Dialog>
    </Paper>
  );
}

function DetailLine({
  label,
  value,
  multiline = false,
}: {
  label: string;
  value?: string | number | null;
  multiline?: boolean;
}) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" fontWeight={700}>
        {label}
      </Typography>
      <Typography
        sx={{ whiteSpace: multiline ? "pre-wrap" : "normal", minHeight: 22 }}
      >
        {value == null || value === "" ? "未入力" : String(value)}
      </Typography>
    </Box>
  );
}
